import { FIREBASE_CONFIG, RECAPTCHA_SITE_KEY } from '../contact/config';
import { enableAppCheckDebugToken } from '../firebaseDebug';
import { parseLegalDocument } from './parser';
import type { LegalDocument } from './interfaces';

// Loads published legal documents from Firestore on the website's project (`novaeon-web`).
//
// Shared by /legal (PlayerCafe's Terms + Privacy Policy) and /privacy (Novaeon's own policy).
// The documents are NOT bundled, so a correction ships without a deploy — and there is no
// local fallback, deliberately: a stale bundled copy silently standing in for the live one is
// the failure this design exists to prevent.
//
// They must be readable with NO session — App Review opens /legal signed out, and so does
// everyone else. Access is gated by App Check enforcement, not by Security Rules; the rules
// grant public read because they have no other option (the site has no Firebase Auth, and
// rules cannot see App Check).

// Reuses the SITE app, config and App Check provider rather than standing up its own.
//
// That is not tidiness: the site's Firebase clients attest with reCAPTCHA v3 App Check, so a
// plain unattested Firestore client is rejected outright wherever enforcement is on — and it
// fails as an empty page, the same silent mode as a missing read rule. Named app "site",
// matching contact/client.ts, so the two share one instance per page.
//
// Firebase is imported lazily for the same reason the contact and VAE clients do it: the page
// renders, and the bundle loads, before any of it is needed.
const APP_NAME = 'site';

/**
 * Reads one Firestore document and parses the named string fields into LegalDocuments, in the
 * order given. Throws if the document is missing or ANY field fails to parse — a partial load
 * is a failure, not a page with half an agreement on it.
 */
export const loadLegalDocuments = async (
  collection: string,
  documentId: string,
  fields: readonly string[],
): Promise<LegalDocument[]> => {

  const { initializeApp, getApps } = await import('firebase/app');
  const { initializeAppCheck, ReCaptchaV3Provider } = await import('firebase/app-check');
  const { getFirestore, doc, getDoc } = await import('firebase/firestore');

  const existing = getApps().find(app => app.name === APP_NAME);
  const app = existing ?? initializeApp(FIREBASE_CONFIG, APP_NAME);

  // Only when this page owns the app. Initializing App Check twice on one instance throws,
  // and contact/client.ts may already have done it.
  if (!existing && RECAPTCHA_SITE_KEY) {

    enableAppCheckDebugToken(process.env.REACT_APP_SITE_APPCHECK_DEBUG_TOKEN);
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  }

  const snapshot = await getDoc(doc(getFirestore(app), collection, documentId));

  if (!snapshot.exists()) { throw new Error(`legal: ${collection}/${documentId} missing`); }

  const data = snapshot.data();
  const parsed = fields.map(field => parseLegalDocument(data[field]));

  // A null here covers malformed JSON edited in the console, which must not render as a
  // half-empty legal document.
  if (parsed.some(document => document === null)) {
    throw new Error(`legal: ${collection}/${documentId} has a missing or malformed field`);
  }

  return parsed as LegalDocument[];
};
